import { AsyncPipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, Provider, TemplateRef, inject } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { FormPlaceholderComponent } from "rl-common/components/form-placeholder/form-placeholder.component";
import { FormPlaceholderLayout, IFormPlaceholderAdvanced } from "rl-common/components/form-placeholder/form-placeholder.models";
import { ICharacteristicMetaData } from "rl-common/models/i-characteristic-meta-data";
import { ICharacteristicTemplate } from "rl-common/models/i-characteristic-template";
import { ComponentChanges } from "rl-common/models/i-component-change";
import { CharDataType, CharTypeId, SystemIndicators } from "rl-common/rl-common.consts";
import { AccountingProcessesService } from "rl-common/services/accounting-processes/accounting-processes.service";
import { AccountingProcessActiveIndicator } from "rl-common/services/accounting-processes/models/accounting-process-active-indicator";
import { IAccountingContact } from "rl-common/services/accounting-processes/models/i-accounting-contact";
import { IAccountingOperation } from "rl-common/services/accounting-processes/models/i-accounting-operation";
import { IAccountingProcessAccount } from "rl-common/services/accounting-processes/models/i-accounting-process-account";
import { IAccountingProcessAccountGroup } from "rl-common/services/accounting-processes/models/i-accounting-process-account-group";
import { IAccountingProcessDetails } from "rl-common/services/accounting-processes/models/i-accounting-process-details";
import { ICreateAccountingOperation } from "rl-common/services/accounting-processes/models/i-create-accounting-operation";
import { ICreateAccountingProcess } from "rl-common/services/accounting-processes/models/i-create-accounting-process";
import { OneConfigService } from "rl-common/services/one-config/one-config.service";
import { PromptService } from "rl-common/services/prompt/prompt.service";
import { of, Subscription } from "rxjs";
import { filter, finalize, map, switchMap, take } from "rxjs/operators";
import { AccountingProcessSteps, IBuildAccountingProcessResult } from "../accounting-processes.models";
import { BuildAccountingProcessService } from "../build-accounting-process-modal/build-accounting-process.service";
import { FfAccountingOperationsComponent } from "../ff-accounting-operations/ff-accounting-operations.component";
import { FfBuildAccountingOperationComponent } from "../ff-build-accounting-operation/ff-build-accounting-operation.component";
import { FFBuildAccountingProcessModalAdapter, IFfBuildAccountingProcessModalComponent } from "./ff-build-accounting-process-modal.adapter";
export interface IAccountingProcessForm {
	name: FormControl<string>;
}

@Component({
	selector: "rl-ff-build-accounting-process-modal",
	templateUrl: "./ff-build-accounting-process-modal.component.html",
	styleUrls: ["./ff-build-accounting-process-modal.component.scss"],
	providers: [BuildAccountingProcessService],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [ReactiveFormsModule, FfAccountingOperationsComponent, FfBuildAccountingOperationComponent, AsyncPipe, FormPlaceholderComponent]
})
export class FfBuildAccountingProcessModalComponent implements OnInit, OnChanges, OnDestroy, IFfBuildAccountingProcessModalComponent {
	private readonly _formBuilder = inject(FormBuilder);
	private readonly _oneConfig = inject(OneConfigService);
	private readonly _promptService = inject(PromptService);
	private readonly _accountingProcessService = inject(AccountingProcessesService);
	private readonly _buildAccountingProcessService = inject(BuildAccountingProcessService);


	@Input()
	selectedContact: IAccountingContact;

	@Input()
	accountingProcessId: string;

	@Input()
	createOperation = false;

	@Input()
	editOperationId: string;

	@Output()
	templatesDateCmdsChange = new EventEmitter<{ [key: string]: ICharacteristicMetaData[] }>();

	@Output()
	anchorTemplateChange = new EventEmitter<ICharacteristicTemplate>();

	@Output()
	onClose = new EventEmitter<IBuildAccountingProcessResult>();

	isNewAccountingProcess = true;
	accounts: IAccountingProcessAccount[] = [];
	accountGroups: IAccountingProcessAccountGroup[] = [];
	accountingProcessDetails: IAccountingProcessDetails;
	isLoadingDetails = false;

	form: FormGroup<IAccountingProcessForm>;
	anchorTemplates: ICharacteristicTemplate[] = [];
	anchorTemplatesDateCmds: { [key: number]: ICharacteristicMetaData[] } = {};
	allocationTemplateSystemIndicator = SystemIndicators.AllocationTemplate;

	openOperationPanels: string[] = [];

	get processName() {
		return this.form.controls.name.value;
	}

	status$ = this._buildAccountingProcessService.status$;
	isSaving$ = this._buildAccountingProcessService.status$.pipe(map(x => x.isSaving));

	currentStep: AccountingProcessSteps = AccountingProcessSteps.AccountingProcess;

	formPlaceholderLayout = FormPlaceholderLayout;
	shinyLoading: IFormPlaceholderAdvanced = {
		rows: [
			{
				columns: [
					{
						width: "20px",
						height: "29px",
						isShiny: false,
					},
					{
						width: "180px",
						height: "29px",
						isShiny: true,
						classList: "me-3"
					},
					{
						height: "29px",
						isShiny: true,
						classList: "w-25"
					},
					{
						height: "29px",
						isShiny: false,
						classList: "flex-grow-1"
					},
				],
				isShiny: false,
				classList: "mb-2"
			},
			{
				columns: [
					{
						width: "200px",
						height: "29px",
						isShiny: true,
						classList: "me-3"
					},
					{
						height: "29px",
						isShiny: true,
						classList: "w-25"
					},
					{
						height: "29px",
						isShiny: false,
						classList: "flex-grow-1"
					},
				],
				isShiny: false
			},
		]
	};

	private readonly _subs: Subscription[] = [];

	ngOnInit(): void {
		if (this.editOperationId) {
			this.openOperationPanels = [this.editOperationId];
		}
		this.isNewAccountingProcess = !this.accountingProcessId;
		if (!this.isNewAccountingProcess) {
			this.currentStep = AccountingProcessSteps.AccountingOperations;
		}
		this.createOperation = this.isNewAccountingProcess || this.createOperation;
		this.anchorTemplates = this._oneConfig.getTemplates(CharTypeId.Amount).filter(x => x.systemIndicator === this.allocationTemplateSystemIndicator);
		this.anchorTemplates.forEach(t => {
			const templateCmds = this._oneConfig.getTemplateCmds(CharTypeId.Amount, t.templateID);
			this.anchorTemplatesDateCmds[t.templateID] = templateCmds.filter(cmd => cmd.dataTypeID === CharDataType.Date);
		});
		this.templatesDateCmdsChange.emit(this.anchorTemplatesDateCmds);
		this.loadAccountingProcessDetails();
		this.loadAccounts();
		this.loadAccountGroups();
	}

	private loadAccountingProcessDetails() {
		if (!this.accountingProcessId) {
			this.accountingProcessDetails = null;
			this.buildAccountingProcessForm();
			return;
		}
		this.isLoadingDetails = true;
		this._accountingProcessService.getAccountingProcessDetails(this.accountingProcessId).pipe(
			finalize(() => this.isLoadingDetails = false)
		).subscribe(details => {
			this.accountingProcessId = details.id;
			this.accountingProcessDetails = details;
			this.buildAccountingProcessForm();
		});
	}

	private loadAccounts() {
		const sub = this._accountingProcessService.getAccounts(this.selectedContact.recordId).subscribe(results => {
			this.accounts = results.map(x => x.account);
			// TODO: block creating accounting process if less than two accounts exist
		});
		this._subs.push(sub);
	}

	private loadAccountGroups() {
		const sub = this._accountingProcessService.getAccountGroups(this.selectedContact.recordId).subscribe(dbgroups => {
			const accountGroups: IAccountingProcessAccountGroup[] = [];
			dbgroups.forEach(gdb => {
				const dealTemplates = gdb.dealTemplateIds.map(t => this._oneConfig.getTemplate(CharTypeId.Deal, t));
				accountGroups.push({
					id: gdb.id,
					groupName: gdb.groupName,
					dealTemplates,
					partyId: gdb.partyId,
					defaultAccountId: gdb.defaultAccountId,
					contactId: gdb.contactId,
					ruleCount: gdb.ruleCount,
					isAssociated: gdb.isAssociated
				} as IAccountingProcessAccountGroup);
			});
			this.accountGroups = accountGroups;
		});
		this._subs.push(sub);
	}

	private buildAccountingProcessForm() {
		this.form = this._formBuilder.group<IAccountingProcessForm>({
			name: new FormControl<string>(this.accountingProcessDetails?.name, [Validators.required]),
		});

		this._buildAccountingProcessService.registerForm(`FfBuildAccountingProcessModalComponent`, this.form);
	}

	onAnchorTemplateChange(template: ICharacteristicTemplate) {
		this.anchorTemplateChange.emit(template);
	}

	ngOnChanges(changes: ComponentChanges<this>): void {
		if (changes.accountingProcessId && changes.accountingProcessId.currentValue !== changes.accountingProcessId.previousValue) {
			this.loadAccountingProcessDetails();
		}
	}

	saveAccountingProcess() {
		if (!this.form.valid) {
			return;
		}

		const process: ICreateAccountingProcess = {
			id: this.accountingProcessId,
			contactRecordId: this.selectedContact.recordId,
			accountingProcessName: this.form.controls.name.value,
			activeIndicator: this.accountingProcessDetails ? this.accountingProcessDetails.activeIndicator : AccountingProcessActiveIndicator.Active,
			xref: null
		};

		this._buildAccountingProcessService.emitStatus({ isSaving: true, message: `Saving Accounting Process` });
		const sub = this._accountingProcessService.upsertAccountingProcess(process)
			.subscribe(details => {
				this._buildAccountingProcessService.emitStatus({ isSaving: false, message: `Saved` });
				this.accountingProcessDetails = details;
				this.accountingProcessId = details.id;
				this.proceedToOperations();
				this.buildAccountingProcessForm();
			});
		this._subs.push(sub);
	}

	private emitClose() {
		this.onClose.next({
			isNewAccountingProcess: this.isNewAccountingProcess,
			selectedContact: this.selectedContact,
			accountingProcessId: this.accountingProcessId
		});
	}

	close(dialogTemplate: TemplateRef<unknown>) {
		this._buildAccountingProcessService.status$.pipe(
			take(1),
			switchMap(status => {
				if (status.isDirty) {
					return this._promptService.confirmMini("Confirmation", dialogTemplate);
				}
				return of(true);
			}),
			filter(confirm => confirm)
		).subscribe(() => {
			this.emitClose();
		});
	}

	proceedToOperations() {
		this.currentStep = AccountingProcessSteps.AccountingOperations;
	}

	addAnotherOperation() {
		this.createOperation = true;
		this.loadAccountingProcessDetails();
		this._buildAccountingProcessService.addAnotherOperation$.next();
	}

	operationDeleted(_operation: IAccountingOperation) {
		this.loadAccountingProcessDetails();
	}

	operationSaved(isNew: boolean, event: ICreateAccountingOperation) {
		const op = this.accountingProcessDetails.operations.find(x => x.id == event.id);
		if (!op) {
			const newOp: IAccountingOperation = event as IAccountingOperation;
			this.accountingProcessDetails.operations.push(newOp);
		}

		// force ngOnChanges to run in build-accounting-operation after saving
		this.accountingProcessDetails = Object.assign({}, this.accountingProcessDetails);

		if (isNew) {
			this.createOperation = false;
		}
	}

	ngOnDestroy(): void {
		this._subs.forEach(sub => sub.unsubscribe());
	}

}

export const FF_BUILDACCOUNTINGPROCESS_MODAL_PROVIDER: Provider = {
	provide: FFBuildAccountingProcessModalAdapter,
	useFactory: () => new FFBuildAccountingProcessModalAdapter(FfBuildAccountingProcessModalComponent)
};

